import React, { useEffect, useState, useCallback } from "react";
import { Col, Row, Modal, Button, Card } from "react-bootstrap";
import useApiServices from "../../utils/useApiServices";
import { DateTime } from "luxon";
import swal from "sweetalert";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import {
  FETCH_ALL_MERCHANT_VALIDATIONS,
  GENERATE_ARCHIVED_REPORT,
  GET_ALL_ARCHIVED_REPORTS,
  DOWNLOAD_ARCHIVED_REPORT,
} from "../../utils/constant";
import Loader from "../Loader/Loader";
import Shimmer from "../../common/Shimmer/Shimmer";
import IconWrapper from "../../common/IconWrapper/IconWrapper";
import { Box } from "@mui/material";
import { convertDateFormat } from "../../utils/helper";
import Cookies from "js-cookie";

const DownloadCSV = ({ toggleRightBar }) => {
  const token = localStorage.getItem("token") || Cookies.get("access_token");
  const { apiCallFnforGet, apiCallFnforPost } = useApiServices();

  // Table state
  const [archivedData, setArchivedData] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);
  const [zoomCount, setZoomCount] = useState(16);

  // Form/Modal state
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [type, setType] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [merchants, setMerchants] = useState([]);
  const [createStartDate, setCreateStartDate] = useState("");
  const [createEndDate, setCreateEndDate] = useState("");
  const [searchOption3, setSearchOption3] = useState("");
  const [searchValue3, setSearchValue3] = useState("");
  const [selectAllMerchants, setSelectAllMerchants] = useState(false);

  const reportstype = [
    "payin",
    "payout",
    "chargeback",
    "withdraw",
    "reconcilation",
    "settlement",
    "wallet",
  ];

  const fetchArchivedReports = useCallback(async () => {
    setTableLoading(true);
    try {
      const response = await apiCallFnforGet(GET_ALL_ARCHIVED_REPORTS);
      if (response?.statusCode === 200) {
        const mappedData = response.data.map((item, index) => ({
          ...item,
          id: item.requestId || index,
          reportType: item.reportType?.toUpperCase(),
          statusText: item.status?.toUpperCase(),
          requestedAtFormatted: item.requestedAt ? convertDateFormat(item.requestedAt.split("T")[0]) + " " + item.requestedAt.split("T")[1]?.split(".")[0] : "-",
          completedAtFormatted: item.completedAt ? convertDateFormat(item.completedAt.split("T")[0]) + " " + item.completedAt.split("T")[1]?.split(".")[0] : "-",
        }));
        setArchivedData(mappedData);
      } else {
        setArchivedData([]);
      }
    } catch (error) {
      console.error("Error fetching archived reports:", error);
      setArchivedData([]);
    } finally {
      setTableLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getMerchantData = useCallback(async () => {
    try {
      const response = await apiCallFnforGet(FETCH_ALL_MERCHANT_VALIDATIONS);
      if (response?.statusCode === 200) {
        setMerchants(response?.data);
      } else {
        setMerchants([]);
      }
    } catch (error) {
      setMerchants([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchArchivedReports();
    getMerchantData();
  }, [fetchArchivedReports, getMerchantData]);

  const handleDateInputChange = (date) => {
    const inputDateTime = DateTime.fromISO(date, { zone: "UTC" });
    const today = DateTime.now().startOf("day");
    return inputDateTime.toISODate() > today.toISODate();
  };

  const handleFromToDateInputChange = (from, to) => {
    const inputFromDateTime = DateTime.fromISO(from, { zone: "utc" });
    const inputToDateTime = DateTime.fromISO(to, { zone: "utc" });
    if (inputFromDateTime > inputToDateTime) return false;
    if (!inputFromDateTime.isValid || !inputToDateTime.isValid || from === to) return false;
    return true;
  };

  const handleDownloadFile = async (requestId) => {
    setLoading(true);
    try {
      const response = await fetch(`${DOWNLOAD_ARCHIVED_REPORT}${requestId}`, {
        method: "GET",
        headers: {
          Authentication: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        if (blob.size > 0) {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `Report_${requestId}.zip`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          swal({ title: "Success", text: "Report downloaded successfully", icon: "success" });
        } else {
          swal({ title: "Warning", text: "The report file is empty.", icon: "warning" });
        }
      } else {
        const errorText = await response.text();
        swal({ title: "Error", text: errorText || "Failed to download report", icon: "error" });
      }
    } catch (error) {
      swal({ title: "Error", text: "An error occurred while fetching the report.", icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateRequest = async () => {
    if (!type) {
      swal({ title: "Warning", text: "Report Type is mandatory.", icon: "warning" });
      return;
    }

    let isDateValid = false;
    if (searchOption3 === "date" && searchValue3) {
      isDateValid = true;
    } else if (searchOption3 === "dateInBetween" && createStartDate && createEndDate) {
      if (!handleFromToDateInputChange(createStartDate, createEndDate) && createStartDate !== createEndDate) {
        swal({ title: "Warning", text: "Start Date can't be greater than End Date!", icon: "warning" });
        return;
      }
      isDateValid = true;
    }

    if (!isDateValid) {
      swal({ title: "Warning", text: "Please select a valid Date or Date Range.", icon: "warning" });
      return;
    }

    if (!selectAllMerchants && !selectedCompany) {
      swal({ title: "Warning", text: "Merchant is mandatory (or Select All Merchants).", icon: "warning" });
      return;
    }

    setLoading(true);

    let payload = {
      mid: selectAllMerchants ? "" : selectedCompany,
      type: type,
      amount: 0.0,
      startAmount: 0.0,
      endAmount: 0.0,
      date: searchOption3 === "date" ? searchValue3 : "",
      startDate: searchOption3 === "dateInBetween" ? createStartDate : "",
      endDate: searchOption3 === "dateInBetween" ? createEndDate : "",
      email: "",
    };

    try {
      const resData = await apiCallFnforPost(GENERATE_ARCHIVED_REPORT, payload);

      if (resData?.statusCode === 200 || resData?.statusCode === 202) {
        swal({
          title: "Success",
          text: resData?.message || "Report Request Submitted Successfully",
          icon: "success",
        }).then(() => {
          setShowModal(false);
          // Reset Form
          setType("");
          setSelectedCompany("");
          setCreateStartDate("");
          setCreateEndDate("");
          setSearchOption3("");
          setSearchValue3("");
          setSelectAllMerchants(false);
          fetchArchivedReports();
        });
      } else {
        swal({ title: "Alert", text: resData?.message || "An error occurred", icon: "error" });
      }
    } catch (error) {
      swal({ title: "Alert", text: error?.response?.data?.message || "An error occurred.", icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: "requestId", headerName: "Request ID", minWidth: 280 },
    { field: "reportType", headerName: "Report Type", minWidth: 150 },
    {
      field: "status", headerName: "Status", minWidth: 140, renderCell: (params) => (
        <span className={`badge badge-rounded ${params?.value === 'COMPLETED' ? 'badge-success' : params?.value === 'FAILED' ? 'badge-danger' : 'badge-warning'}`}>
          {params?.value?.toUpperCase()}
        </span>
      )
    },
    { field: "requestedAtFormatted", headerName: "Requested At", minWidth: 200 },
    { field: "completedAtFormatted", headerName: "Completed At", minWidth: 200 },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 280,
      sortable: false,
      renderCell: (params) => (
        <div className="d-flex align-items-center gap-2 py-1">
          <button
            className="badge badge-rounded badge-success mx-2 px-3 py-2 border-0"
            onClick={() => handleDownloadFile(params?.row?.requestId)}
            disabled={params.row.status !== "COMPLETED"}
            style={{ opacity: params.row.status === "COMPLETED" ? 1 : 0.5, cursor: params.row.status === "COMPLETED" ? "pointer" : "not-allowed" }}
          >
            Download & View
          </button>
        </div>
      ),
    },
  ];

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
        <Box sx={{ flexGrow: 1 }} />
        <div className="d-flex justify-content-start"><GridToolbarQuickFilter /></div>
      </GridToolbarContainer>
    );
  }

  const HandleReset = () => {
    fetchArchivedReports();
  };

  const incrementZoom = () => {
    if (zoomCount < 28) setZoomCount(zoomCount + 1);
  };

  const decrementZoom = () => {
    if (zoomCount > 14) setZoomCount(zoomCount - 1);
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <section className="admin-dashboard-sec">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div className='d-flex align-items-center'>
                    <button
                      type="button"
                      className="btn-close m-3"
                      onClick={toggleRightBar}
                    ></button>
                    <div className="fs-2 text-dark fw-bold">Archived Reports</div>
                  </div>
                  <div className="col-7 mb-4 mb-sm-0" style={{ textAlign: "right" }}>
                    <IconWrapper title={"Zoomin"}>
                      <span onClick={incrementZoom}>
                        <i className="fa-solid fa-magnifying-glass-plus"></i>{" "}
                      </span>
                    </IconWrapper>
                    <IconWrapper title={"Zoomout"}>
                      <span onClick={decrementZoom}>
                        <i className="fa-solid fa-magnifying-glass-minus"></i>{" "}
                      </span>
                    </IconWrapper>
                    <IconWrapper title={"Refresh"}>
                      <i className="fa fa-refresh" onClick={HandleReset}></i>
                    </IconWrapper>
                    <IconWrapper title={"Request New Report"}>
                      <i className="fa fa-plus-circle text-primary" style={{ cursor: 'pointer' }} onClick={() => setShowModal(true)}></i>
                    </IconWrapper>
                  </div>
                </div>

                <div className="dasboard-view-count-boxes">
                  <Card.Body>
                    <div className="report-grid-view">
                      <div className="material-table-records mb-3" style={{ height: "550px" }}>
                        {tableLoading ? (
                          <Shimmer type="table" />
                        ) : (
                          <DataGrid
                            rows={archivedData}
                            columns={columns}
                            slots={{
                              toolbar: CustomToolbar
                            }}
                            hideFooterPagination={false}
                            pageSizeOptions={[20, 50, 100]}
                            initialState={{
                              pagination: { paginationModel: { pageSize: 20 } },
                            }}
                            sx={{
                              fontSize: `${zoomCount}px`,
                              "& .MuiDataGrid-columnHeader": {
                                color: "#495057",
                                fontWeight: "700",
                                display: "block"
                              },
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </Card.Body>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      {/* Request Report Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-0 pb-0 px-4 pt-4">
          <Modal.Title className="fw-bold text-primary">Generate Archive Report</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {loading && <Loader />}

          <fieldset className="border p-4 rounded mb-4">
            <legend className="float-none w-auto px-3 fs-6 fw-bold text-muted">Merchant & Report Type</legend>
            <Row className="g-3">
              <Col md={6}>
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <label className="form-label fw-bold small mb-0">Select Individual Merchant</label>
                  <div className="form-check d-flex align-items-center m-0">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="selectAllMerchantsModal"
                      checked={selectAllMerchants}
                      style={{ width: "16px", height: "16px", cursor: "pointer" }}
                      onChange={(e) => {
                        setSelectAllMerchants(e.target.checked);
                        if (e.target.checked) setSelectedCompany("");
                      }}
                    />
                    <label className="form-check-label ms-1 small text-muted mb-0" htmlFor="selectAllMerchantsModal" style={{ cursor: "pointer", fontSize: "11px" }}>
                      Select All
                    </label>
                  </div>
                </div>
                <select
                  className="form-control form-select shadow-none"
                  onChange={(e) => setSelectedCompany(e.target.value)}
                  value={selectedCompany}
                  disabled={selectAllMerchants}
                >
                  <option value="">-- Choose Merchant --</option>
                  {merchants?.map((m) => (
                    <option key={m.mid} value={m.mid}>
                      {m.mid} - {m.merchantName}
                    </option>
                  ))}
                </select>
              </Col>

              <Col md={6}>
                <label className="form-label fw-bold small mb-1">Report Category <span className="text-danger">*</span></label>
                <select
                  className="form-control form-select shadow-none"
                  onChange={(e) => setType(e.target.value)}
                  value={type}
                >
                  <option value="">-- Choose Report Type --</option>
                  {reportstype?.map((r) => (
                    <option key={r} value={r}>{r.toUpperCase()} Report</option>
                  ))}
                </select>
              </Col>
            </Row>
          </fieldset>

          <fieldset className="border p-4 rounded">
            <legend className="float-none w-auto px-3 fs-6 fw-bold text-muted">Time Period</legend>
            <Row className="g-3">
              <Col md={4}>
                <label className="form-label fw-bold small mb-1">Mode <span className="text-danger">*</span></label>
                <select
                  className="form-control form-select shadow-none"
                  value={searchOption3}
                  onChange={(e) => setSearchOption3(e.target.value)}
                >
                  <option value="">Choose Mode</option>
                  <option value="date">Specific Date</option>
                  <option value="dateInBetween">Date Range</option>
                </select>
              </Col>

              <Col md={8}>
                {searchOption3 === "date" && (
                  <div className="form-group">
                    <label className="form-label fw-bold small mb-1">Date</label>
                    <input
                      className="form-control shadow-none"
                      type="date"
                      value={searchValue3}
                      onChange={(e) => {
                        if (handleDateInputChange(e.target.value)) {
                          swal("Warning", "Future dates not allowed", "warning");
                          setSearchValue3("");
                        } else setSearchValue3(e.target.value);
                      }}
                    />
                  </div>
                )}
                {searchOption3 === "dateInBetween" && (
                  <Row className="g-2">
                    <Col md={6}>
                      <label className="form-label fw-bold small mb-1">From</label>
                      <input
                        className="form-control shadow-none"
                        type="date"
                        value={createStartDate}
                        onChange={(e) => {
                          if (handleDateInputChange(e.target.value)) {
                            swal("Warning", "Invalid Date", "warning");
                            setCreateStartDate("");
                          } else setCreateStartDate(e.target.value);
                        }}
                      />
                    </Col>
                    <Col md={6}>
                      <label className="form-label fw-bold small mb-1">To</label>
                      <input
                        className="form-control shadow-none"
                        type="date"
                        value={createEndDate}
                        onChange={(e) => {
                          if (handleDateInputChange(e.target.value)) {
                            swal("Warning", "Invalid Date", "warning");
                            setCreateEndDate("");
                          } else setCreateEndDate(e.target.value);
                        }}
                      />
                    </Col>
                  </Row>
                )}
              </Col>
            </Row>
          </fieldset>
        </Modal.Body>
        <Modal.Footer className="border-0 px-4 pb-4 pt-0">
          <Button variant="outline-secondary" className="rounded-pill px-4" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button variant="success" className="rounded-pill px-4 shadow" onClick={handleGenerateRequest} disabled={loading}>
            Generate Report Request
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DownloadCSV;
